/**
 * "How billing works": when Revvly invoices a subscription's base price and
 * each usage charge, which component settings decide it, and what a person has
 * to do before an invoice can go out.
 *
 * Shown in two places that must say the same thing: the guide opened from the
 * dashboard's Catalog page and the website's billing timing guide. The Revvly
 * Agent reads the same rules from its own copy,
 * `apps/panel-api/src/agent/tools/billing-guide.ts` (the API does not import
 * shared libraries at runtime). Change a rule in one, change it in both, and in
 * the dashboard's per-subscription explainer
 * (`admin-dashboard/src/components/BillingExplainer/billingExplainer.ts`).
 */

export interface BillingGuideSection {
  id: string;
  title: string;
  points: string[];
}

export interface BillingGuideSetting {
  /** The field name as it appears on a Variable Component. */
  field: string;
  title: string;
  text: string;
}

export interface BillingGuideRecipe {
  /** What is being charged for. */
  charge: string;
  timing: 'ADVANCE' | 'ARREARS';
  resets: 'STANDING' | 'PER_PERIOD';
  /** What a period with nothing reported means; null where it never applies. */
  ifSilent: 'REVIEW' | 'ZERO' | null;
  usageCapture: 'API' | 'Manual' | 'Either';
  invoiced: string;
  needsSomeone: string;
}

export const BILLING_GUIDE_INTRO =
  'Every subscription bills its base price ahead of each period. Each usage charge is billed either ahead with the base or after the period ends, and a charge counted per period needs a final figure before its invoice goes out. These rules decide what each invoice carries and when.';

export const BILLING_GUIDE_SECTIONS: BillingGuideSection[] = [
  {
    id: 'invoices',
    title: 'When each invoice goes out',
    points: [
      'The base price is always billed ahead: on the first day for the first period, then for each next period at renewal.',
      'Invoice timing sets when a renewal’s ahead invoice is raised: a number of days before the renewal date, or on the renewal date when it is 0. Each client can have its own value per billing period; otherwise the workspace default applies, and otherwise 0. A client set to 0 keeps invoicing on the renewal date whatever the workspace says.',
      'A usage charge billed in advance goes on the same ahead invoice as the base, at the quantity the period starts with.',
      'A usage charge billed in arrears goes on the renewal invoice for the period that just ended, so its first charge comes one full period after the subscription starts.',
      'With invoice timing above 0 and a charge billed after the period, the client gets two invoices each period: the base and advance charges a few days before renewal, then usage once the period has ended. With invoice timing 0, both are on one renewal invoice.',
      'A free trial raises no invoice until it converts. A subscription awaiting go-live bills nothing until it is activated.',
    ],
  },
  {
    id: 'readings',
    title: 'Readings: the final figure for a period',
    points: [
      'Only a charge counted per period needs a reading. A standing quantity bills its level and never waits on anyone.',
      'When a period closes, its reading waits 24 hours for late reports, then seals and is billed. The renewal invoice stays a draft until every reading on it is sealed or waived.',
      'With invoice timing 0, the base price is on that same renewal invoice and waits with the usage. With a lead, the base already went out, so only the usage waits.',
      'Usage capture API (from the integration): if nothing was reported, a charge set to “Hold the invoice for review” raises a task and the invoice waits; one set to “Bill zero automatically” bills zero.',
      'Usage capture Manual (entered by someone): someone enters the figure after each period. A task is raised and the invoice waits for it.',
      'When an API charge closes at zero, workspace admins are warned before it bills and can enter a figure or hold it for review. Where zero is normal for one subscription, the warning can be switched off on that subscription’s component.',
    ],
  },
];

export const BILLING_GUIDE_SETTINGS: BillingGuideSetting[] = [
  {
    field: 'billingTiming',
    title: 'Advance or arrears',
    text: 'Billed ahead with the base, or after the period ends. New components default to advance, so set arrears for anything counted during the period.',
  },
  {
    field: 'resetBehaviour',
    title: 'Standing or per period',
    text: 'Standing carries forward from one period to the next (seats, licences). Per period starts again at zero every period (API calls, tokens, hours) and is always billed after the period.',
  },
  {
    field: 'billedValue',
    title: 'Which figure is charged',
    text: 'The peak, the total, or the level at period end. A per-period count is charged on its total.',
  },
  {
    field: 'unreportedUsagePolicy',
    title: 'What silence means',
    text: 'For a per-period count only. “Hold the invoice for review” (the default) waits until someone enters or waives the figure. “Bill zero automatically” treats a quiet period as no usage.',
  },
];

export const BILLING_GUIDE_CAPTURE_NOTE =
  'Usage capture is set on the client, not in the catalog: API (from the integration) or Manual (entered by someone), API when unset. It can be changed for one component on one subscription.';

export const BILLING_GUIDE_RECIPES: BillingGuideRecipe[] = [
  {
    charge: 'Seats or users paid upfront',
    timing: 'ADVANCE',
    resets: 'STANDING',
    ifSilent: null,
    usageCapture: 'Either',
    invoiced: 'With the base, for the period about to start',
    needsSomeone: 'No',
  },
  {
    charge: 'Licences or locations in use, paid at period end',
    timing: 'ARREARS',
    resets: 'STANDING',
    ifSilent: null,
    usageCapture: 'Either',
    invoiced: 'On the renewal invoice, for the period that ended',
    needsSomeone: 'No',
  },
  {
    charge: 'API calls, tokens or transactions sent by an integration',
    timing: 'ARREARS',
    resets: 'PER_PERIOD',
    ifSilent: 'REVIEW',
    usageCapture: 'API',
    invoiced: 'After the period, once the reading seals (about a day)',
    needsSomeone: 'Only if nothing is reported',
  },
  {
    charge: 'Optional usage where a quiet period is normal',
    timing: 'ARREARS',
    resets: 'PER_PERIOD',
    ifSilent: 'ZERO',
    usageCapture: 'API',
    invoiced: 'After the period; nothing reported bills zero',
    needsSomeone: 'No',
  },
  {
    charge: 'Hours, deliverables or anything entered by hand',
    timing: 'ARREARS',
    resets: 'PER_PERIOD',
    ifSilent: 'REVIEW',
    usageCapture: 'Manual',
    invoiced: 'After the period, once someone enters or waives it',
    needsSomeone: 'Yes, every period',
  },
];

/** Plain labels for the recipe table's enum values. */
export const BILLING_GUIDE_LABELS: Record<string, string> = {
  ADVANCE: 'Advance',
  ARREARS: 'Arrears',
  STANDING: 'Standing',
  PER_PERIOD: 'Per period',
  REVIEW: 'Hold for review',
  ZERO: 'Bill zero',
};
